import { CustomerOrderBase } from '../../contracts/customer-order.js';
import { MapUtils } from '../../utility/map-utils.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { PaymentProvider } from './payment-provider.js';
import { PaymentStatus } from './payment-status.js';
import {
  cloneMap, DataModel, DataModelBuilder, isSameData, isSameMap, mapToData, SerializationContext, SerializedData,
} from '../dto/dto.js';

export class CheckoutRequestResult extends DataModel {
  static readonly TYPE_ID: string = 'cf93ca2588ae41fe8b36b0e53d5cec5b';

  constructor(
    public readonly order: CustomerOrderBase,
    public readonly status: PaymentStatus,
    public readonly provider: PaymentProvider,
    public readonly paymentRequestId: string | null = null,
    public readonly paymentId: string | null = null,
    public readonly checkoutSessionId: string | null = null,
    public readonly data: Map<string, unknown> = new Map<string, unknown>(),
  ) {
    super();
  }

  get $typeId() {
    return CheckoutRequestResult.TYPE_ID;
  }

  get isFailure() {
    return this.status === PaymentStatus.Failed || this.status === PaymentStatus.Canceled;
  }

  static fromMap(context: SerializationContext, map: SerializedData): CheckoutRequestResult {
    return new CheckoutRequestResult(
        context.fromData<CustomerOrderBase>(MapUtils.get<SerializedData>(map, 'order')),
        MapUtils.get<PaymentStatus>(map, 'status'),
        MapUtils.get<PaymentProvider>(map, 'provider'),
        MapUtils.getOrNull<string>(map, 'paymentRequestId'),
        MapUtils.getOrNull<string>(map, 'paymentId'),
        MapUtils.getOrNull<string>(map, 'checkoutSessionId'),
        MapUtils.getMap(map, 'data', (k, v) => {
          return { key: k, value: v };
        }),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'order': context.toData(this.order),
      'status': this.status,
      'provider': this.provider,
      'paymentRequestId': this.paymentRequestId,
      'paymentId': this.paymentId,
      'checkoutSessionId': this.checkoutSessionId,
      'data': mapToData(this.data, (k, v) => {
        return { key: k, value: v };
      }),
    };
  }

  clone(): this {
    return new CheckoutRequestResult(
        this.order.clone(),
        this.status,
        this.provider,
        this.paymentRequestId,
        this.paymentId,
        this.checkoutSessionId,
        cloneMap(this.data),
    ) as this;
  }

  isSame(other: CheckoutRequestResult): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.order, this.order) &&
      isSameMap(other.data, this.data) &&
      this.status === other.status &&
      this.provider === other.provider &&
      this.paymentRequestId === other.paymentRequestId &&
      this.paymentId === other.paymentId &&
      this.checkoutSessionId === other.checkoutSessionId;
  }

  getData<T>(key: string, defValue?: T): T {
    const value = this.data.get(key);

    if(isNotNullOrUndefined(value)) {
      return value as T;
    }

    if(isNotNullOrUndefined(defValue)) {
      return defValue;
    }

    throw new Error(`Missing or null field with key {${key}} in checkout data.`);
  }

  getDataOrNull<T>(key: string): T | null {
    const value = this.data.get(key);
    // eslint-disable-next-line no-undefined
    if(value !== undefined) {
      if(value === null) return null;
      return value as T;
    }

    return null;
  }
}

export class CheckoutRequestResultBuilder extends DataModelBuilder<CheckoutRequestResult> {
  build(context: SerializationContext, map: SerializedData): CheckoutRequestResult {
    return CheckoutRequestResult.fromMap(context, map);
  }
}
